import { cva, VariantProps } from 'class-variance-authority';
import { forwardRef, memo, useState, useRef, useCallback, KeyboardEvent } from 'react';
import { XIcon } from '@page/icons';

import { cn } from '../../lib/styles';
import { Label } from '../../designSystem/Label';
import { IconButton } from '../../designSystem/IconButton';

const tagInputVariants = cva(
  'font-body-md font-medium text-text-primary placeholder:text-text-tertiary transition-all',
  {
    variants: {
      variant: {
        default:
          'bg-bg-inter-secondary-default rounded-sm outline-1 outline-border-default focus-within:outline-1 focus-within:outline-border-input-select',
        unstyled: 'border-none outline-none',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export type TagInputProps = Omit<React.ComponentProps<'input'>, 'size' | 'ref' | 'value' | 'onChange'> &
  VariantProps<typeof tagInputVariants> & {
    label?: React.ReactNode;
    error?: React.ReactNode;
    containerClassName?: string;
    inputClassName?: string;
    tags?: string[];
    onTagsChange?: (tags: string[], targetTag: string) => void;
    maxTags?: number;
    placeholder?: string;
    disabled?: boolean;
  };

const TagInputBase = (
  {
    id,
    className,
    type = 'text',
    variant,
    label,
    error,
    containerClassName,
    inputClassName,
    tags = [],
    onTagsChange,
    maxTags,
    placeholder = 'Type and press Enter to add tags...',
    disabled,
    ...rest
  }: TagInputProps,
  ref: React.ForwardedRef<HTMLInputElement>,
) => {
  const actualId = id || rest.name;
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  const addTag = useCallback(
    (tag: string) => {
      const trimmedTag = tag.trim();
      if (trimmedTag && !tags.includes(trimmedTag) && (!maxTags || tags.length < maxTags)) {
        const newTags = [...tags, trimmedTag];
        onTagsChange?.(newTags, trimmedTag);
        setInputValue('');
      }
    },
    [tags, maxTags, onTagsChange],
  );

  const removeTag = useCallback(
    (tagToRemove: string) => {
      const newTags = tags.filter((tag) => tag !== tagToRemove);
      onTagsChange?.(newTags, tagToRemove);
    },
    [tags, onTagsChange],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        addTag(inputValue);
      } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
        e.preventDefault();
        removeTag(tags[tags.length - 1]);
      }
    },
    [inputValue, tags, addTag, removeTag],
  );

  const handleInputBlur = useCallback(() => {
    if (inputValue.trim()) {
      addTag(inputValue);
    }
  }, [inputValue, addTag]);

  const inputElement = (
    <div
      className={cn(
        'flex flex-wrap items-start gap-1 w-full min-h-10 p-1',
        tagInputVariants({ variant }),
        error && 'border-text-destructive',
        disabled && 'opacity-50 cursor-not-allowed',
        label ? inputClassName : className,
        containerClassName,
      )}
    >
      {tags.map((tag, index) => (
        <div
          key={`${tag}-${index}`}
          className={cn(
            'inline-flex items-center gap-1 p-2 rounded-sm text-sm font-medium',
            'bg-bg-sur-primary hover:bg-bg-sur-secondary',
            'transition-all',
            disabled && 'opacity-50 cursor-not-allowed',
            'flex-shrink-0',
          )}
        >
          <span className="font-body-sm font-semibold text-text-secondary truncate max-w-32">{tag}</span>
          <IconButton size="sm" onClick={() => removeTag(tag)} disabled={disabled} className="h-4 w-4 p-0">
            <XIcon className="size-4 fill-icon-secondary" />
          </IconButton>
        </div>
      ))}

      <input
        ref={(node) => {
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
          inputRef.current = node;
        }}
        id={actualId}
        type={type}
        data-slot="input"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleInputBlur}
        placeholder={tags.length === 0 ? placeholder : ''}
        disabled={disabled}
        className={cn(
          'flex-1 min-w-0 bg-transparent border-none outline-none placeholder:text-text-tertiary font-body-md font-medium',
          'disabled:pointer-events-none disabled:cursor-not-allowed',
          'py-1 h-8',
          {
            'mx-1!': tags.length === 0,
            'mx-0.5!': tags.length > 0,
          },
        )}
        {...rest}
      />
    </div>
  );

  if (!label) return inputElement;

  return (
    <div className={cn('flex flex-col', className)}>
      <Label htmlFor={actualId} className="mb-2">
        {label}
      </Label>

      {inputElement}

      <div className="h-5 pt-1 text-text-destructive font-body-sm">{error}</div>
    </div>
  );
};

export const TagInput = memo(forwardRef(TagInputBase));
